import * as mapboxgl from 'mapbox-gl';
import AdFormService from '../services/AdFormService';
import Logging from '../Logging';
import AdLocationData from '../types/AdLocationData';
import AdCoordinates from '../AdCoordinates';
import {Section} from "../services/Section";
import MapData from "../types/MapData";

export default class LocationMap {
    private readonly mapStaticImage: HTMLImageElement;

    private readonly viewMapButton: HTMLElement;

    private readonly accessToken: string;

    private readonly adLocationData: AdLocationData;

    private readonly streetZoomLevel: number;

    private readonly districtZoomLevel: number;

    private readonly localityZoomLevel: number;
    private readonly country: string;
    private mapData: MapData;
    private readonly pageViewType: string;
    private readonly pageViewId: string;
    private readonly id: string;
    private readonly enableApproximateArea: boolean;
    private readonly mapElement: HTMLElement | null;

    constructor(
        {mapData, country, pageViewType, pageViewId, id} : {mapData: MapData, country: string, pageViewType: string, pageViewId: string, id: string}) {
        this.mapStaticImage = document.getElementById('map-static-image') as HTMLImageElement;
        this.viewMapButton = document.getElementById('view-map-button') as HTMLElement;
        this.accessToken = (mapData.accessToken && mapData.accessToken !== '') ? mapData.accessToken : 'pk.eyJ1IjoibG9ra3VtYXBib3giLCJhIjoiY2t5YjcybHlrMGNqMTJ2cGJxdTF0NXA4aiJ9.xj2Td6NHaabLEFvQNPfjFg';
        this.adLocationData = mapData.adLocationData;
        this.streetZoomLevel = 15;
        this.districtZoomLevel = 14;
        this.localityZoomLevel = 13;
        this.country = country;
        this.mapData = mapData;
        this.pageViewType = pageViewType;
        this.pageViewId = pageViewId;
        this.id = id
        this.enableApproximateArea = mapData.enableApproximateArea ? mapData.enableApproximateArea : false;
        this.mapElement = document.getElementById('location-map');
    }

    private setup = () => {
        if (window.IntersectionObserver) {
            const observer = new IntersectionObserver((entries, observer) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        this.lazyLoadMap();
                        observer.unobserve(entry.target);
                    }
                });
            }, {rootMargin: '0px 0px 100px 0px'});
            const locationMap = document.getElementById('location-map-wrapper');
            if (locationMap) observer.observe(locationMap);
        }
    };

    static init({mapData, country, pageViewType, pageViewId, id}: {mapData: MapData, country: string, pageViewType: string, pageViewId: string, id: string}) {
        if (mapData == undefined) return;
        if (!mapData.showMap) return;

        new LocationMap({mapData, country, pageViewType, pageViewId, id}).setup();
    }

    private lazyLoadMap() {
        this.getCoordinatesFromLocation(this.adLocationData)
            .then((coordinatesFromLocation) => {
                if (this.isCoordinateValid(coordinatesFromLocation.longitude, coordinatesFromLocation.latitude)) {
                    this.removeNoLocationAvailableElement();
                    this.showLocationMapElement();
                    const zoom = (this.mapData.zoom && this.mapData.zoom > 0) ? this.mapData.zoom : this.getProperZoomLevel();
                    this.trackMapImpressionAfterLoadingMap();
                    this.setupStaticMapEventListener(coordinatesFromLocation, zoom);
                } else {
                    this.removeStaticMapComponents();
                    this.showNoLocationAvailableElement();
                }
            });
    }


    private async getCoordinatesFromLocation(adLocationData: AdLocationData) {
        if (this.isCoordinateValid(adLocationData.coordinates.longitude, adLocationData.coordinates.latitude)) return adLocationData.coordinates;

        if (!this.isEmpty(this.adLocationData.address)) {
            const coordinatesFromLocation = await this.getCoordinatesFromMapBoxWithLocation(this.buildGeocodingApiQueryAddress());
            if (this.isCoordinateValid(coordinatesFromLocation.longitude, coordinatesFromLocation.latitude)) return coordinatesFromLocation;
        }

        if (!this.isEmpty(this.adLocationData.province)) {
            const coordinatesFromLocation = await this.getCoordinatesFromMapBoxWithLocation(this.buildGeocodingApiQuery());
            if (this.isCoordinateValid(coordinatesFromLocation.longitude, coordinatesFromLocation.latitude)) return coordinatesFromLocation;
        }

        if (this.mapElement && this.mapElement.dataset.location && !this.isEmpty(this.mapElement.dataset.location)) {
            const coordinatesFromLocation = await this.getCoordinatesFromMapBoxWithLocation(this.buildGeocodingByLocationDataApiQuery());
            if (this.isCoordinateValid(coordinatesFromLocation.longitude, coordinatesFromLocation.latitude)) return coordinatesFromLocation;
        }

        return new AdCoordinates('0.0', '0.0');
    }

    private async getCoordinatesFromMapBoxWithLocation(location: string): Promise<AdCoordinates> {
        return this.getMapBoxLatitudeLongitudeFrom(location)
            .then((longLat) => {
                if (longLat) {
                    const long = longLat[0].toString();
                    const lat = longLat[1].toString();
                    return new Promise((resolve) => {
                        resolve(new AdCoordinates(lat, long));
                    });
                }
                return new Promise((resolve) => {
                    resolve(new AdCoordinates('0.0', '0.0'));
                });
            });
    }

    private async getMapBoxLatitudeLongitudeFrom(query: string) {
        const mapBoxGeocodingApiUrl = 'https://api.mapbox.com/geocoding/v5/mapbox.places/';
        const res = await fetch(`${mapBoxGeocodingApiUrl + query}.json?access_token=${this.accessToken}&country=${this.country}`);
        const geocodingResponse = await res.json();
        if (geocodingResponse.features.length > 0) {
            return geocodingResponse.features[0].center;
        }
    }

    private getProperZoomLevel(): number {
        if (this.isCoordinateValid(this.adLocationData.coordinates.longitude, this.adLocationData.coordinates.latitude)) return this.streetZoomLevel;
        if (!this.isEmpty(this.adLocationData.address) || !this.isEmpty(this.adLocationData.district) || !this.isEmpty(this.adLocationData.postcode)) return this.districtZoomLevel;
        return this.localityZoomLevel;
    }

    private setupStaticMapEventListener = (coordinates: AdCoordinates, zoom: number) => {
        if (this.viewMapButton != null) {
            this.viewMapButton.addEventListener('click', () => {
                this.setupMap(coordinates, zoom);
            });

        }

        this.mapStaticImage.addEventListener('dragstart', () => {
            this.setupMap(coordinates, zoom);
        });

    };

    private setupMap = (coordinates: AdCoordinates, zoom: number) => {
        const map = this.buildMap(coordinates.longitude, coordinates.latitude, zoom);
        const showPin = this.shouldShowPin(this.mapData.visibility, this.adLocationData.address);
        const showApproximateArea = this._shouldShowApproximateArea(this.mapData.visibility, this.adLocationData.address);
        if (showPin) {
            this.buildPin(map, coordinates.longitude, coordinates.latitude);
        } else if (showApproximateArea) {
            this._buildApproximateArea(map, coordinates.longitude, coordinates.latitude);
        }
        this.removeStaticMapComponents();
    };

    private buildMap = (longitude: string, latitude: string, zoom: number) => {
        let lastZoom = zoom;
        const map = new mapboxgl.Map({
            accessToken: this.accessToken,
            container: 'map',
            style: 'mapbox://styles/mapbox/streets-v11',
            center: [parseFloat(longitude), parseFloat(latitude)],
            zoom,
        });

        map.on('zoomend', () => {
            const currentZoom = map.getZoom();
            lastZoom = currentZoom;
        });

        if (this.mapData.withControls) {
            map.addControl(new mapboxgl.NavigationControl(), "top-left");
        }

        return map;
    };

    private buildPin(map: mapboxgl.Map, longitude: string, latitude: string) {
        const el = document.createElement('div');
        el.className = 'marker';
        new mapboxgl.Marker(el)
            .setLngLat([parseFloat(longitude), parseFloat(latitude)])
            .addTo(map);
    }

    private isEmpty(value: string): boolean {
        return typeof (value) === undefined || value == null || value == '';
    }

    private shouldShowPin(visibility: string, address: string): boolean {
        return (visibility == 'accurate' || visibility == '') && (this.isCoordinateValid(this.adLocationData.coordinates.longitude, this.adLocationData.coordinates.latitude) || !this.isEmpty(address));
    }

    private _shouldShowApproximateArea(visibility: string, address: string): boolean {
        return this.enableApproximateArea && (visibility != 'accurate') && (this.isCoordinateValid(this.adLocationData.coordinates.longitude, this.adLocationData.coordinates.latitude));
    }

    private isCoordinateValid(longitude: string, latitude: string) {
        return !this.isEmpty(latitude) && !this.isEmpty(longitude) && parseFloat(longitude) != 0 && parseFloat(latitude) != 0
            && parseFloat(longitude) >= -180.0 && parseFloat(longitude) <= 180.0 && parseFloat(latitude) >= -90.0 && parseFloat(latitude) <= 90.0;
    }

    private buildGeocodingApiQuery(): string {
        return `${this.adLocationData.postcode},${this.adLocationData.district},${this.adLocationData.locality},${this.adLocationData.province}`;
    }

    private buildGeocodingByLocationDataApiQuery(): string {
        return `${this.mapElement!.dataset.location}`;
    }

    private buildGeocodingApiQueryAddress(): string {
        return this.adLocationData.address;
    }

    private showLocationMapElement() {
        if (this.mapElement != null) {
            this.mapElement.classList.remove('hidden');
        }
    }

    private showNoLocationAvailableElement() {
        const noLocationWrapper = document.getElementById('no-location-wrapper');
        if (noLocationWrapper != null) {
            this.showLocationMapElement();
            noLocationWrapper.classList.remove('hidden');
        }
    }

    private removeNoLocationAvailableElement() {
        const noLocationWrapper = document.getElementById('no-location-wrapper');
        if (noLocationWrapper != null) {
            this.showLocationMapElement();
            noLocationWrapper.remove();
        }
    }

    private removeStaticMapComponents() {
        this.mapStaticImage.remove();

        if (this.viewMapButton != null) {
            this.viewMapButton.remove()
        }
    }

    private trackMapImpressionAfterLoadingMap() {
        if (this.pageViewType !== 'PROJECT_PAGE_DIRECTORY' && this.pageViewType !== 'PAGEVIEW_PROJECT_PAGE_DIRECTORY') {
            new AdFormService().impression(this.pageViewId, Section.SECTION_ADPAGE_MAP, [this.id])
        } else {
            new AdFormService().impressionProjectLocationMap(this.pageViewId, this.id).catch((error) => Logging.error(error));
        }
    }

    private _buildApproximateArea(map: mapboxgl.Map, longitude: string, latitude: string) {
        const geoJson = this._createGeoJsonCircle(parseFloat(longitude), parseFloat(latitude));
        map.on('load', () => {
            this._addSourceAndLayers(map, geoJson);
        });
    }

    private _createGeoJsonCircle(longitude: number, latitude: number): object {
        let coordinates = this._createApproximateAreaCoordinates(longitude, latitude);
        return {
            type: 'Feature',
            geometry: {
                type: 'Polygon',
                coordinates: [coordinates]
            },
            properties: {
                fill: '#0080ff',
                'fill-opacity': 0.2,
                stroke: '#0080ff'
            }
        };
    }

    private _createApproximateAreaCoordinates(longitude: number, latitude: number): number[][] {
        const circleRadius = (this.mapData.visibility && this.mapData.visibility === 'approximate') ? 0.5 : 1.5;
        const circlePoints = 64;
        const coordinates = {longitude: longitude, latitude: latitude};
        let longitudeConversionConstant = 111.320;
        const convertLongitudeTo2DPlane = circleRadius / (longitudeConversionConstant * Math.cos(coordinates.latitude * Math.PI / 180));
        let latitudeConversionConstant = 110.574;
        const convertLatitudeTo2DPlane = circleRadius / latitudeConversionConstant;

        var returnCoordinates = [];
        var theta, x, y;
        for (var i = 0; i < circlePoints; i++) {
            theta = (i / circlePoints) * (2 * Math.PI);
            x = convertLongitudeTo2DPlane * Math.cos(theta);
            y = convertLatitudeTo2DPlane * Math.sin(theta);

            returnCoordinates.push([coordinates.longitude + x, coordinates.latitude + y]);
        }
        returnCoordinates.push(returnCoordinates[0]);

        return returnCoordinates;
    }

    private _addSourceAndLayers(map: mapboxgl.Map, geoJson: any) {
        const sourceName = "polygon";
        map.addSource(sourceName, {
            "type" : "geojson",
            "data" : {
                "type": "FeatureCollection",
                "features":[
                    geoJson
                ]
            }
        });
        map.addLayer({
            "id": "polygon-fill",
            "type": "fill",
            "source": sourceName,
            "layout": {},
            "paint": {
                "fill-color": "#1f81f7",
                "fill-opacity": 0.2
            }
        });
        map.addLayer({
            "id": "polygon-border",
            "type": "line",
            "source": sourceName,
            "layout": {},
            "paint": {
                "line-color": "#1f81f7",
                "line-width": 1
            }
        });
    }
}
